import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { X } from 'lucide-react';
import { Icon } from '@iconify/react';
import { cn } from '@/lib/utils';
import { StudioSettingsDomain } from './StudioSettingsDomain';
import { StudioSettingsGAds, type GoogleAdsSelectionModeState } from './StudioSettingsGAds';
import { GoogleAdsSelectionPreview } from './GoogleAdsSelectionPreview';
import { StudioSettingsMetadata } from './StudioSettingsMetadata';
import { StudioSettingsAnalytics } from './StudioSettingsAnalytics';
import { useStudioStore } from '@/store/useStudioStore';
import { projectApi } from '@/api/project';
import { siteApi } from '@/api/site';
import { isSpecialProjectType } from '@/lib/projectUtils';
import { trackStudioEvent } from '@/lib/studioTracking';
import { getTrackingErrorCode } from '@/lib/trackingUtils';

interface StudioSettingsDrawerProps {
  isOpen: boolean;
  onClose: () => void;
}

/**
 * StudioSettingsDrawer - 椤圭洰璁剧疆鎶藉眽缁勪欢
 * 鑱岃矗锛氭瀬鑷村鍒?studio.html 涓殑 .cfg-backdrop 鍖哄煙銆?
 * 甯冨眬銆佹牱寮忋€佸浘鏍囧昂瀵稿強棰滆壊鍧囦弗鏍奸伒寰?CSS 鍘熷瀹氫箟銆?
 */
export const StudioSettingsDrawer = ({ isOpen, onClose }: StudioSettingsDrawerProps) => {
  const { t } = useTranslation('studio');
  const navigate = useNavigate();
  const {
    activeSettingsTab,
    setActiveSettingsTab,
    currentProject,
    currentProjectTypeId,
    currentSiteInfo,
    currentId,
    isGadsTagConnected,
    currentSiteId,
    setCurrentSiteInfo,
    setGadsTagConnected,
  } = useStudioStore();
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [isGadsBadgeDone, setIsGadsBadgeDone] = useState(isGadsTagConnected);
  const [gadsSelectionMode, setGadsSelectionMode] = useState<GoogleAdsSelectionModeState | null>(
    null,
  );

  const isDomainBound = currentSiteInfo ? currentSiteInfo.status === 2 : false;
  const isGadsPickerActive = isOpen && activeSettingsTab === 'gads' && Boolean(gadsSelectionMode);
  const effectiveSiteId =
    currentSiteId ??
    (currentProject?.sitePaths?.[0]?.siteId ? String(currentProject.sitePaths[0].siteId) : null);

  const navItems = [
    {
      id: 'seo',
      label: 'Page details',
      icon: 'fa6-solid:gear',
      hasBadge: false,
      isDone: true,
    },
    {
      id: 'domain',
      label: 'Custom domain',
      icon: 'fa6-solid:globe',
      hasBadge: true,
      isDone: isDomainBound,
    },
    {
      id: 'gads',
      label: 'Ad tracking',
      icon: 'fa6-solid:bullhorn',
      hasBadge: true,
      isDone: isGadsBadgeDone,
    },
    {
      id: 'analytics',
      label: 'Analytics',
      icon: 'fa6-solid:chart-line',
      hasBadge: false,
      isDone: true,
    },
  ];

  const handleDeleteProject = async () => {
    const resolvedProjectTypeId =
      currentProjectTypeId ?? currentProject?.projectTypeId ?? currentProject?.projectType;
    const usesProjectApi = isSpecialProjectType(resolvedProjectTypeId);
    const hasId = usesProjectApi ? !!currentProject?.projectId : !!currentId;
    if (!hasId) {
      trackStudioEvent('studio_project_delete_result', {
        result: 'failure',
        projectTypeId: resolvedProjectTypeId,
        errorCode: 'missing_project_id',
      });
      return;
    }
    setDeleting(true);
    try {
      const params = usesProjectApi
        ? { projectId: Number(currentProject!.projectId) }
        : { userProjectId: Number(currentId) };
      await projectApi.deleteProject(params);
      trackStudioEvent('studio_project_delete_result', {
        result: 'success',
        projectTypeId: resolvedProjectTypeId,
      });
      onClose();
      navigate('/dashboard');
    } catch (error) {
      trackStudioEvent('studio_project_delete_result', {
        result: 'failure',
        projectTypeId: resolvedProjectTypeId,
        errorCode: getTrackingErrorCode(error),
      });
      toast.error(t('settings.danger.delete_error'));
      setDeleting(false);
      setConfirmDelete(false);
    }
  };

  const handleTabChange = (toTab: string) => {
    if (toTab === activeSettingsTab) return;

    trackStudioEvent('studio_settings_tab_change', {
      fromTab: activeSettingsTab,
      toTab,
      isDomainBound,
      isGadsTagConnected,
    });
    setActiveSettingsTab(toTab);
  };

  useEffect(() => {
    if (!isOpen) return;
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [isOpen, onClose]);

  useEffect(() => {
    setIsGadsBadgeDone(isGadsTagConnected);
  }, [isGadsTagConnected]);

  useEffect(() => {
    if (!isOpen || activeSettingsTab !== 'gads') {
      setGadsSelectionMode(null);
    }
  }, [activeSettingsTab, isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    let cancelled = false;

    const loadDrawerStatuses = async () => {
      const projectParams = currentId
        ? { userProjectId: Number(currentId) }
        : currentProject?.projectId
          ? { projectId: Number(currentProject.projectId) }
          : null;

      const requests: Array<Promise<void>> = [];

      if (effectiveSiteId) {
        requests.push(
          siteApi
            .getSiteList({ pageNumber: 1, pageSize: 500 })
            .then((response) => {
              if (cancelled) return;
              const site =
                response.list.find((item) => item.id === Number(effectiveSiteId)) ?? null;
              setCurrentSiteInfo(site);
            })
            .catch(() => {
              // Keep existing status when site refresh fails.
            }),
        );
      }

      if (projectParams) {
        requests.push(
          projectApi
            .getGoogleAdsDetail(projectParams)
            .then((config) => {
              if (cancelled) return;
              const hasTag = Boolean(
                config &&
                ((config.configType === 2 && config.tagId?.trim()) ||
                  (config.configType === 1 && config.tagCode?.trim())),
              );
              setGadsTagConnected(hasTag);
              setIsGadsBadgeDone(hasTag);
            })
            .catch(() => {
              // Keep existing badge state when ads config refresh fails.
            }),
        );
      }

      await Promise.all(requests);
    };

    void loadDrawerStatuses();

    return () => {
      cancelled = true;
    };
  }, [
    currentId,
    currentProject?.projectId,
    effectiveSiteId,
    isOpen,
    setCurrentSiteInfo,
    setGadsTagConnected,
  ]);

  if (!isOpen) return null;

  return (
    <div
      className={cn(
        'cfg-backdrop animate-in fade-in fixed inset-0 z-[200] flex items-stretch duration-200',
        isGadsPickerActive
          ? 'backdrop-blur-0 bg-[rgba(15,23,42,0.34)]'
          : 'bg-black/32 backdrop-blur-[2px]',
      )}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      {isGadsPickerActive && gadsSelectionMode ? (
        <div className="absolute top-[18px] right-[calc(min(560px,96vw)+18px)] bottom-[18px] z-[201] hidden w-[min(760px,calc(100vw-min(560px,96vw)-54px))] max-w-[calc(100vw-min(560px,96vw)-54px)] min-w-[280px] flex-col overflow-hidden rounded-[12px] border border-white/45 bg-[#E5E7EB] shadow-[0_24px_70px_rgba(15,23,42,0.32)] lg:flex">
          <div className="flex items-center justify-between gap-[12px] border-b border-[rgba(15,23,42,0.1)] bg-[rgba(255,255,255,0.96)] px-[12px] py-[10px]">
            <span className="text-[12px] font-[850] text-[#0D0D1A]">{gadsSelectionMode.title}</span>
            <span className="text-[10px] font-[800] tracking-[0.4px] text-[#0D0D1A]/45 uppercase">
              {gadsSelectionMode.stateText}
            </span>
          </div>

          <div className="relative min-h-0 flex-1">
            <GoogleAdsSelectionPreview isActive={isGadsPickerActive} />
          </div>
        </div>
      ) : null}

      <div
        className="cfg-drawer animate-in slide-in-from-right pointer-events-auto ml-auto flex h-full w-full max-w-[560px] flex-col bg-white shadow-[-12px_0_40px_rgba(0,0,0,0.12)] duration-[250ms] ease-out"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Head: .cfg-drawer-head */}
        <div className="cfg-drawer-head border-border flex shrink-0 items-center justify-between border-b p-[18px_22px_14px]">
          <div className="cfg-drawer-title flex items-center text-[16px] font-[800] tracking-[-0.3px] text-[#0D0D1A]">
            <Icon icon="fa6-solid:sliders" className="mr-2 text-[13px] text-[#6D28D9]" />
            Page setup
          </div>
          <button
            onClick={onClose}
            className="cfg-drawer-close border-border flex h-[30px] w-[30px] items-center justify-center rounded-[8px] border bg-white text-[#0D0D1A]/56 transition-all hover:bg-black/5"
          >
            <X size={13} />
          </button>
        </div>

        {/* Body: .cfg-drawer-body */}
        <div className="cfg-drawer-body flex min-h-0 flex-1 overflow-hidden">
          {/* Left Nav: .cfg-nav */}
          <nav className="cfg-nav border-border flex w-[180px] shrink-0 flex-col gap-[2px] overflow-y-auto border-r p-[14px_10px]">
            {navItems.map((item) => (
              <button
                key={item.id}
                onClick={() => handleTabChange(item.id)}
                className={cn(
                  'cfg-nav-item group flex items-start gap-[9px] rounded-[9px] border-none bg-transparent p-[9px_12px] text-left font-sans text-[13px] leading-[1.35] font-[600] text-[#0D0D1A]/56 transition-all',
                  activeSettingsTab === item.id && 'bg-[#6D28D9]/[0.09] font-[700] text-[#6D28D9]',
                )}
              >
                <div className="mt-[1px] flex w-[16px] shrink-0 justify-center">
                  <Icon
                    icon={item.icon}
                    className={cn(
                      'text-[13px]',
                      activeSettingsTab === item.id ? 'text-[#6D28D9]' : 'text-[#0D0D1A]/56',
                    )}
                  />
                </div>
                <span className="min-w-0 flex-1 break-words whitespace-normal">{item.label}</span>
                {item.hasBadge && (
                  <span
                    className={cn(
                      'cfg-nav-badge mt-[4px] ml-auto h-[7px] w-[7px] shrink-0 rounded-full',
                      item.isDone ? 'bg-emerald-500' : 'bg-[#D97706]',
                    )}
                  />
                )}
              </button>
            ))}
          </nav>

          {/* Sections: .cfg-sections */}
          <div className="cfg-sections flex-1 overflow-x-hidden overflow-y-auto p-[22px]">
            {activeSettingsTab === 'domain' && <StudioSettingsDomain />}
            {activeSettingsTab === 'gads' && (
              <StudioSettingsGAds
                onTagConnectionChange={setIsGadsBadgeDone}
                onSelectionModeChange={setGadsSelectionMode}
              />
            )}
            {activeSettingsTab === 'seo' && <StudioSettingsMetadata />}
            {activeSettingsTab === 'analytics' && <StudioSettingsAnalytics />}
          </div>
        </div>

        {/* Footer: .cfg-save-bar */}
        <div className="cfg-save-bar border-border flex shrink-0 items-center justify-between gap-[10px] border-t p-[14px_22px]">
          {confirmDelete ? (
            <div className="flex items-center gap-[8px]">
              <button
                className="flex shrink-0 cursor-pointer items-center gap-[5px] rounded-[7px] border border-[#DC2626]/30 bg-[#DC2626]/[0.06] px-[10px] py-[5px] font-sans text-[12px] font-[700] text-[#DC2626] transition-colors hover:bg-[#DC2626]/[0.12] disabled:opacity-50"
                onClick={handleDeleteProject}
                disabled={deleting}
              >
                <Icon icon="fa6-solid:trash-can" className="text-[12px]" />
                {deleting ? t('settings.delete_deleting') : t('settings.delete_confirm')}
              </button>
              <button
                className="font-sans text-[12px] text-[#0D0D1A]/40 transition-colors hover:text-[#0D0D1A] disabled:opacity-50"
                onClick={() => setConfirmDelete(false)}
                disabled={deleting}
              >
                {t('publish.cancel')}
              </button>
            </div>
          ) : (
            <button
              className="cfg-delete-link flex shrink-0 cursor-pointer items-center gap-[5px] border-none bg-transparent p-0 font-sans text-[12px] font-medium text-[#0D0D1A]/40 transition-colors hover:text-[#DC2626]"
              onClick={() => setConfirmDelete(true)}
            >
              <Icon icon="fa6-solid:trash-can" className="text-[12px]" />
              {t('settings.delete_page')}
            </button>
          )}
          <button
            className="cfg-save-btn flex h-[36px] cursor-pointer items-center justify-center rounded-[9px] bg-[linear-gradient(135deg,#6D28D9,#0891B2)] px-[22px] font-sans text-[13px] font-[700] text-white transition-opacity hover:opacity-[0.88]"
            onClick={onClose}
          >
            {t('settings.close')}
          </button>
        </div>
      </div>
    </div>
  );
};
